import { Badge, Box, Code, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import type { OrderLifecycleRefund } from '@/types'

interface OrderRefundsSectionProps {
  refunds?: OrderLifecycleRefund[]
  locale?: string
  t: TFunction
  formatAmount: (value?: string | null) => string
  formatTimestamp: (value?: string | null, locale?: string) => string | null
  truncateMiddle: (value?: string | null, length?: number) => string
  formatStatus: (status: string | undefined, translate: TFunction) => string
}

export const OrderRefundsSection = ({
  refunds,
  locale,
  t,
  formatAmount,
  formatTimestamp,
  truncateMiddle,
  formatStatus,
}: OrderRefundsSectionProps) => {
  if (!refunds || refunds.length === 0) {
    return <Text fontSize="sm">{t('orders.lifecycle.noData')}</Text>
  }

  return (
    <VStack align="stretch" spacing={4}>
      {refunds.map(refund => (
        <Box key={refund.id} borderWidth="1px" borderRadius="md" p={4}>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text fontWeight="medium">{refund.id}</Text>
              <Badge colorScheme={refund.isVerified ? 'green' : 'orange'}>
                {formatStatus(refund.status, t)}
              </Badge>
            </HStack>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} fontSize="sm">
              <Text>
                {t('orders.lifecycle.amountUsd')}: {formatAmount(refund.amountUsd)}
              </Text>
              <Text>
                {t('orders.lifecycle.initiator')}: {refund.initiator ?? '-'}
              </Text>
              <Text>
                {t('orders.lifecycle.txHash')}: {truncateMiddle(refund.txHash)}
              </Text>
              <Text>
                {t('orders.lifecycle.verifiedAt')}:{' '}
                {formatTimestamp(refund.timestamps?.verifiedAt, locale) ?? t('common.na')}
              </Text>
            </SimpleGrid>
            {Array.isArray(refund.meta) && refund.meta.length > 0 && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  {t('orders.lifecycle.userPayments')}
                </Text>
                <VStack align="stretch" spacing={1} fontSize="sm">
                  {refund.meta.map((item, index) => (
                    <HStack key={`${item.to ?? 'meta'}-${index}`} spacing={3}>
                      <Code fontSize="xs">{truncateMiddle(item.to)}</Code>
                      <Badge>{formatAmount(item.amountUsd)}</Badge>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}
            {refund.notes && (
              <Text fontSize="sm">
                {t('orders.lifecycle.notes')}: {refund.notes}
              </Text>
            )}
          </VStack>
        </Box>
      ))}
    </VStack>
  )
}
